import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { BadgeCheck, FileSearch } from "lucide-react";
import api from "@/lib/api";

const EASE = [0.16, 1, 0.3, 1];

export const LabReports = () => {
  const [reports, setReports] = useState(null);

  useEffect(() => {
    api.get("/reports").then((r) => setReports(r.data)).catch(() => setReports([]));
  }, []);

  return (
    <section id="lab-reports" data-testid="lab-reports-section" className="relative py-28 md:py-40 border-t border-[#E4E4E7]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16"
        >
          <div>
            <p className="font-mono-clinical text-xs tracking-[0.3em] uppercase text-[#6D28D9] mb-4">03 — Verification</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight uppercase">
              Janoshik Verified.<br />Dosed as labelled.
            </h2>
          </div>
          <p className="text-[#71717A] text-sm md:text-base max-w-sm">
            Every batch is independently assayed by Janoshik Analytical. Measured content is published against the
            labelled claim — no underfilled vials, ever.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
          className="border border-[#E4E4E7] overflow-x-auto"
        >
          <table className="w-full text-sm min-w-[860px]">
            <thead>
              <tr className="bg-[#FFFFFF] border-b border-[#E4E4E7] font-mono-clinical text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA]">
                <th className="text-left px-5 py-4">Compound</th>
                <th className="text-left px-5 py-4">Batch</th>
                <th className="text-left px-5 py-4">Labelled</th>
                <th className="text-left px-5 py-4">Measured</th>
                <th className="text-left px-5 py-4">Purity</th>
                <th className="text-left px-5 py-4">Report №</th>
                <th className="text-left px-5 py-4">Date</th>
              </tr>
            </thead>
            <tbody>
              {reports === null ? (
                <tr><td colSpan={7} className="px-5 py-10 text-center text-[#A1A1AA] font-mono-clinical text-xs animate-pulse">Loading reports…</td></tr>
              ) : (
                reports.map((r, i) => (
                  <motion.tr
                    key={r.id}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.04 }}
                    data-testid={`lab-report-row-${r.product_code}`}
                    className="border-b border-[#E4E4E7] hover:bg-[#F4F4F5] transition-colors"
                  >
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2.5">
                        <BadgeCheck className="w-4 h-4 text-[#6D28D9] shrink-0" strokeWidth={1.5} />
                        <div>
                          <span className="font-display font-bold uppercase text-[#09090B] block leading-tight">{r.product_name}</span>
                          <span className="font-mono-clinical text-[10px] text-[#A1A1AA]">{r.lab}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 font-mono-clinical text-xs text-[#71717A]">{r.batch}</td>
                    <td className="px-5 py-4 font-mono-clinical text-xs text-[#71717A]">{r.claimed_content}</td>
                    <td className="px-5 py-4 font-mono-clinical text-xs font-bold text-[#6D28D9] drop-shadow-[0_0_12px_rgba(109,40,217,0.25)]">{r.measured_content}</td>
                    <td className="px-5 py-4 font-mono-clinical text-xs text-[#3F3F46]">{r.purity}</td>
                    <td className="px-5 py-4 font-mono-clinical text-xs text-[#71717A]">
                      <span className="inline-flex items-center gap-1.5"><FileSearch className="w-3.5 h-3.5 text-[#A1A1AA]" />{r.task_no}</span>
                    </td>
                    <td className="px-5 py-4 font-mono-clinical text-xs text-[#A1A1AA]">{r.test_date}</td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="mt-6 font-mono-clinical text-[11px] tracking-[0.15em] uppercase text-[#A1A1AA]"
        >
          Verify any report number directly with the issuing laboratory. Method: HPLC / MS unless noted.
        </motion.p>
      </div>
    </section>
  );
};
